import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Dropdown, Form, Modal } from "react-bootstrap";
import {
  FaFilePdf,
  FaVideo,
  FaFileAlt,
  FaPlay,
  FaTrash,
  FaEdit,
  FaUsers,
  FaPlus,
  FaSearch,
  FaEye,
  FaDownload,
  FaCalendarAlt,
  FaBookOpen,
  FaTimes,
  FaExternalLinkAlt,
} from "react-icons/fa";
import api, { getFileUrl } from "../../api";
import PageTitle from "../layouts/PageTitle";
import swal from "sweetalert";
import usePermission from "../../hooks/usePermission";

const CATEGORIES = [
  { id: "all", label: "All Categories" },
  { id: "general", label: "General" },
  { id: "onboarding", label: "Onboarding" },
  { id: "technical", label: "Technical" },
  { id: "admin", label: "Admin" },
  { id: "safety", label: "Safety" },
];

const categoryBadgeMap = {
  general: { bg: "secondary", text: "white", label: "General" },
  onboarding: { bg: "info", text: "white", label: "Onboarding" },
  technical: { bg: "warning", text: "dark", label: "Technical" },
  admin: { bg: "danger", text: "white", label: "Admin" },
  safety: { bg: "success", text: "white", label: "Safety" },
};

const UserGuides = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canManageGuides =
    hasPermission("create:userguides") || hasPermission("manage:userguides");

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  // State for reading full text guide
  const [readingGuide, setReadingGuide] = useState(null);

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const res = await api.get("/user-guides");
      if (res.data?.success) setGuides(res.data.data || []);
    } catch (err) {
      console.error("fetchGuides error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (guide) => {
    navigate(`/user-guides/edit/${guide._id}`);
  };

  const handleDelete = async (guideId) => {
    const confirmed = await swal({
      title: "Delete this guide?",
      text: "This guide and its attachments will be permanently deleted!",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    try {
      await api.delete(`/user-guides/${guideId}`);
      swal("Deleted!", "Guide deleted successfully!", "success");
      fetchGuides();
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to delete", "error");
    }
  };

  const handleDownload = async (e, fileUrl, rawFileName) => {
    e.preventDefault();
    e.stopPropagation();
    const url = getFileUrl(fileUrl);
    const fileName = rawFileName || fileUrl.split("/").pop() || "Document";

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (_) {
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const filteredGuides = useMemo(() => {
    return guides.filter((g) => {
      const matchesSearch =
        !searchQuery ||
        g.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.content?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === "all" || g.category === selectedCategory;

      const matchesType = selectedType === "all" || g.type === selectedType;

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [guides, searchQuery, selectedCategory, selectedType]);

  const svgMenu = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <circle fill="#6c757d" cx="5" cy="12" r="2.2" />
        <circle fill="#6c757d" cx="12" cy="12" r="2.2" />
        <circle fill="#6c757d" cx="19" cy="12" r="2.2" />
      </g>
    </svg>
  );

  return (
    <Fragment>
      <PageTitle activeMenu="User Guides" motherMenu="Help & Support" />

      {/* Main Container */}
      <div className="mb-5">
        {/* Top Header Card */}
        <Card className="border-0 shadow-sm rounded-4 mb-4">
          <Card.Body className="p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex align-items-center">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "rgba(58, 122, 254, 0.1)",
                    color: "#3a7afe",
                  }}
                >
                  <FaBookOpen className="fs-4" />
                </div>
                <div>
                  <h4 className="fw-bold mb-1 text-dark">
                    User Guides &amp; Documentation
                  </h4>
                  <p className="text-muted small mb-0">
                    Comprehensive standard procedures, PDF manuals, and video walkthroughs.
                  </p>
                </div>
              </div>

              {canManageGuides && (
                <Button
                  variant="primary"
                  className="rounded-pill px-4 py-2 fw-semibold shadow-sm d-flex align-items-center"
                  onClick={() => navigate("/user-guides/add")}
                >
                  <FaPlus className="me-2" /> Add Guide
                </Button>
              )}
            </div>

            <hr className="my-3 opacity-25" />

            {/* Filter and Search Bar */}
            <Row className="g-3 align-items-center">
              {/* Search input */}
              <Col lg={4} md={6}>
                <div className="input-group" style={{ height: "42px" }}>
                  <span
                    className="input-group-text border-end-0 text-muted"
                    style={{ borderColor: "#d7dae3", borderRadius: "8px 0 0 8px" }}
                  >
                    <FaSearch />
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="Search guides by title or keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      height: "42px",
                      borderColor: "#d7dae3",
                      borderRadius: searchQuery ? "0px" : "0 8px 8px 0",
                      fontSize: "0.92rem",
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-start-0"
                      onClick={() => setSearchQuery("")}
                      style={{
                        borderColor: "#d7dae3",
                        borderRadius: "0 8px 8px 0",
                      }}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </Col>

              {/* Type Filter */}
              <Col lg={3} md={6}>
                <Form.Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  style={{
                    height: "42px",
                    borderRadius: "8px",
                    borderColor: "#d7dae3",
                    fontSize: "0.92rem",
                  }}
                >
                  <option value="all">All Formats (Text, PDF, Video)</option>
                  <option value="text">📝 Step-by-Step Text</option>
                  <option value="pdf">📄 PDF Documents</option>
                  <option value="video">🎥 Video Tutorials</option>
                </Form.Select>
              </Col>

              {/* Category Pills */}
              <Col lg={5} xs={12}>
                <div className="d-flex flex-wrap gap-1 align-items-center justify-content-lg-end">
                  {CATEGORIES.map((cat) => {
                    const isActive = selectedCategory === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold transition-all ${
                          isActive
                            ? "btn-primary shadow-sm"
                            : "btn-outline-secondary border-0 bg-light text-muted"
                        }`}
                        style={{ fontSize: "0.82rem" }}
                      >
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Guides Grid */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="mt-2 text-muted fw-semibold">Loading user guides...</p>
          </div>
        ) : filteredGuides.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-4 text-center py-5">
            <Card.Body>
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "#f8f9fa",
                  color: "#a0aabc",
                }}
              >
                <FaFileAlt size={28} />
              </div>
              <h5 className="fw-bold text-dark mb-1">No user guides found</h5>
              <p className="text-muted small mb-3">
                {searchQuery || selectedCategory !== "all" || selectedType !== "all"
                  ? "Try clearing your filters or search keywords."
                  : "No guides published yet."}
              </p>
              {canManageGuides && (
                <Button
                  variant="primary"
                  className="rounded-pill px-4"
                  onClick={() => navigate("/user-guides/add")}
                >
                  <FaPlus className="me-2" /> Create First Guide
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {filteredGuides.map((guide) => {
              const catConfig =
                categoryBadgeMap[guide.category] || categoryBadgeMap.general;

              const isEveryone =
                !guide.allowedRoles || guide.allowedRoles.length === 0;

              return (
                <Col md={6} xl={4} key={guide._id}>
                  <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden d-flex flex-column">
                    {/* Top Decorative Header */}
                    <div className="p-3 pb-2 d-flex justify-content-between align-items-center border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        {/* Type Icon Badge */}
                        <div
                          className="rounded-2 p-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor:
                              guide.type === "pdf"
                                ? "rgba(220, 53, 69, 0.1)"
                                : guide.type === "video"
                                ? "rgba(13, 110, 253, 0.1)"
                                : "rgba(25, 135, 84, 0.1)",
                            color:
                              guide.type === "pdf"
                                ? "#dc3545"
                                : guide.type === "video"
                                ? "#0d6efd"
                                : "#198754",
                          }}
                        >
                          {guide.type === "pdf" && <FaFilePdf size={18} />}
                          {guide.type === "video" && <FaVideo size={18} />}
                          {guide.type === "text" && <FaFileAlt size={18} />}
                        </div>

                        {/* Category Tag */}
                        <Badge
                          bg={catConfig.bg}
                          text={catConfig.text}
                          className="px-2 py-1 rounded-pill fw-semibold text-uppercase"
                          style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}
                        >
                          {catConfig.label}
                        </Badge>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        {/* Audience Badge */}
                        <Badge
                          bg={isEveryone ? "success" : "info"}
                          bgOpacity={15}
                          className={`px-2 py-1 rounded-pill fw-normal ${
                            isEveryone
                              ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                              : "bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25"
                          }`}
                          style={{ fontSize: "0.74rem" }}
                        >
                          <FaUsers className="me-1" />
                          {isEveryone
                            ? "Everyone"
                            : `${guide.allowedRoles.length} Roles`}
                        </Badge>

                        {/* Actions Menu */}
                        {canManageGuides && (
                          <Dropdown align="end">
                            <Dropdown.Toggle
                              as="div"
                              className="btn-link p-1 text-muted no-caret rounded-circle hover-bg"
                              style={{ cursor: "pointer", lineHeight: 1 }}
                            >
                              {svgMenu}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="shadow-sm border-0 rounded-3">
                              <Dropdown.Item onClick={() => handleEdit(guide)}>
                                <FaEdit className="me-2 text-primary" /> Edit Guide
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => handleDelete(guide._id)}
                              >
                                <FaTrash className="me-2" /> Delete Guide
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </div>
                    </div>

                    {/* Card Body */}
                    <Card.Body className="p-4 d-flex flex-column flex-grow-1">
                      {/* Title */}
                      <h5 className="fw-bold text-dark mb-2 text-truncate" title={guide.title}>
                        {guide.title}
                      </h5>

                      {/* Description */}
                      <p
                        className="text-muted small mb-3 flex-grow-1"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "38px",
                          lineHeight: "1.5",
                        }}
                      >
                        {guide.description || "No description provided."}
                      </p>

                      {/* CONTENT MEDIA AREA */}

                      {/* 1. TEXT GUIDE */}
                      {guide.type === "text" && (
                        <div className="mb-3">
                          <div
                            className="p-3 rounded-3 border small text-secondary user-guide-text-box"
                            style={{
                              maxHeight: "110px",
                              overflow: "hidden",
                              position: "relative",
                              fontSize: "0.85rem",
                              lineHeight: "1.5",
                            }}
                          >
                            <div style={{ whiteSpace: "pre-line" }}>
                              {guide.content}
                            </div>
                            <div className="user-guide-fade" />
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="w-100 mt-2 rounded-pill fw-semibold"
                            onClick={() => setReadingGuide(guide)}
                          >
                            <FaBookOpen className="me-2" /> Read Full Guide
                          </Button>
                        </div>
                      )}

                      {/* 2. PDF GUIDE */}
                      {guide.type === "pdf" && guide.fileUrl && (
                        <div className="mb-3">
                          <div className="p-3 border rounded-3 d-flex align-items-center justify-content-between shadow-none user-guide-media-box">
                            <div className="d-flex align-items-center overflow-hidden me-2">
                              <div
                                className="rounded-2 p-2 me-2 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  backgroundColor: "rgba(220, 53, 69, 0.1)",
                                  color: "#dc3545",
                                  width: "36px",
                                  height: "36px",
                                }}
                              >
                                <FaFilePdf size={18} />
                              </div>
                              <div className="overflow-hidden">
                                <div
                                  className="fw-bold text-dark text-truncate small"
                                  title={guide.fileUrl.split("/").pop()}
                                >
                                  {guide.fileUrl.split("/").pop()}
                                </div>
                                <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                  PDF Document
                                </small>
                              </div>
                            </div>

                            <div className="d-flex gap-1 flex-shrink-0">
                              <a
                                href={getFileUrl(guide.fileUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: "32px", height: "32px" }}
                                title="Open / Preview PDF"
                              >
                                <FaEye size={13} />
                              </a>
                              <button
                                type="button"
                                onClick={(e) => handleDownload(e, guide.fileUrl)}
                                className="btn btn-sm btn-outline-success rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: "32px", height: "32px" }}
                                title="Download PDF"
                              >
                                <FaDownload size={13} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 3. VIDEO - FILE UPLOAD */}
                      {guide.type === "video" && guide.fileUrl && (
                        <div className="mb-3">
                          <video
                            controls
                            className="w-100 rounded-3 border"
                            style={{ maxHeight: "150px", backgroundColor: "#000" }}
                            src={getFileUrl(guide.fileUrl)}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}

                      {/* 4. VIDEO - EXTERNAL URL */}
                      {guide.type === "video" && !guide.fileUrl && guide.videoUrl && (
                        <div className="mb-3">
                          <a
                            href={guide.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm w-100 rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center shadow-sm"
                          >
                            <FaPlay className="me-2" /> Watch Video Tutorial
                            <FaExternalLinkAlt className="ms-2" size={12} />
                          </a>
                        </div>
                      )}

                      {/* Footer Info */}
                      <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center text-muted small">
                        <span>
                          <FaCalendarAlt className="me-1 opacity-75" />
                          {new Date(guide.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                        {guide.createdBy?.name && (
                          <span className="text-truncate" style={{ maxWidth: "120px" }}>
                            By {guide.createdBy.name}
                          </span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* MODAL TO READ FULL TEXT GUIDE */}
      {readingGuide && (
        <Modal
          show={Boolean(readingGuide)}
          onHide={() => setReadingGuide(null)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-bottom py-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <Badge
                  bg={
                    categoryBadgeMap[readingGuide.category]?.bg || "secondary"
                  }
                  className="rounded-pill px-2 py-1 text-uppercase"
                  style={{ fontSize: "0.72rem" }}
                >
                  {readingGuide.category}
                </Badge>
                <small className="text-muted">
                  <FaCalendarAlt className="me-1" />
                  {new Date(readingGuide.createdAt).toLocaleDateString()}
                </small>
              </div>
              <Modal.Title className="h5 fw-bold mb-0 text-dark">
                {readingGuide.title}
              </Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {readingGuide.description && (
              <div className="p-3 mb-4 rounded-3 border bg-light text-muted small">
                {readingGuide.description}
              </div>
            )}
            <div
              className="guide-content-preview text-dark"
              style={{
                whiteSpace: "pre-wrap",
                lineHeight: "1.7",
                fontSize: "0.95rem",
              }}
            >
              {readingGuide.content}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top py-2">
            <Button
              variant="secondary"
              className="rounded-pill px-4"
              onClick={() => setReadingGuide(null)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Fragment>
  );
};

export default UserGuides;
